/**
 * Applies a side-panel edit to a custom issue field (an `upsertEntityFieldItem` or
 * `removeEntityFieldItem` on `issueFieldValues`) to one board card.
 *
 * The edit names the field by GraphQL node ID and holds the issue viewer's value node. The card
 * keys the field `field.<databaseId>` and holds the board payload's value, so the view's field
 * definitions say which slot the edit is for.
 *
 * Returns the same card when nothing applies, so a card the edit doesn't change keeps its identity.
 */

import type {EntityFieldItemOperation} from '@github-ui/optimistic-connection'

import {type FieldDefinition, FieldDataType} from '../../../saved-view-options/fields'
import type {BoardPayloadItem, BoardPayloadSelectOption, CustomFieldIdentifier} from '../board-payload'
import {isCustomFieldIdentifier} from '../visible-card-fields'

const DATA_TYPES_BY_TYPENAME: Readonly<Record<string, string>> = {
  IssueFieldTextValue: FieldDataType.TEXT,
  IssueFieldSingleSelectValue: FieldDataType.SINGLE_SELECT,
  IssueFieldMultiSelectValue: FieldDataType.MULTI_SELECT,
  IssueFieldDateValue: FieldDataType.DATE,
  IssueFieldNumberValue: FieldDataType.NUMBER,
}

export function applyCustomFieldEditToCard(
  item: BoardPayloadItem,
  operation: EntityFieldItemOperation,
  fieldDefinitions: readonly FieldDefinition[],
): BoardPayloadItem {
  if (item.type !== 'issue' || operation.field !== 'issueFieldValues') return item

  const removed = operation.type === 'removeEntityFieldItem'
  const dataType = removed ? undefined : DATA_TYPES_BY_TYPENAME[String(operation.item.__typename)]
  if (!removed && dataType === undefined) return item

  const identifier = findCustomField(fieldDefinitions, operation, dataType)
  if (identifier === undefined || fieldDefinitions.some(field => field.identifier === identifier && field.deferred)) {
    return item
  }

  const fields: Record<string, {value: unknown} | undefined> = item.fields
  let value: unknown
  if (removed) {
    if (fields[identifier] === undefined) return item
    value = null
  } else {
    value = toCardValue(operation.item, dataType, fields[identifier]?.value)
    if (value === undefined) return item
  }

  // Written even when the board left the slot out: the card only draws a custom field the view shows.
  return {...item, fields: {...item.fields, [identifier]: {value}}}
}

/**
 * The definition with the edit's GraphQL node ID. A server that does not send node IDs yet is
 * matched by name and data type, and only when exactly one definition fits: names are unique
 * within an org, but a view can span several.
 */
function findCustomField(
  fieldDefinitions: readonly FieldDefinition[],
  operation: EntityFieldItemOperation,
  dataType: string | undefined,
): CustomFieldIdentifier | undefined {
  const custom = fieldDefinitions.filter(definition => isCustomFieldIdentifier(definition.identifier))

  const byId = custom.find(definition => definition.globalId === operation.key)
  if (byId !== undefined) return byId.identifier as CustomFieldIdentifier
  if (custom.some(definition => definition.globalId !== undefined)) return undefined

  const field = operation.item.field
  const name = isRecord(field) && typeof field.name === 'string' ? field.name.toLowerCase() : undefined
  if (name === undefined) return undefined

  const [match, ...others] = custom.filter(
    definition =>
      definition.label.toLowerCase() === name && (dataType === undefined || definition.dataType === dataType),
  )
  return match !== undefined && others.length === 0 ? (match.identifier as CustomFieldIdentifier) : undefined
}

function toCardValue(node: Record<string, unknown>, dataType: string | undefined, current: unknown): unknown {
  switch (dataType) {
    case FieldDataType.TEXT:
      return typeof node.value === 'string' || node.value === null ? node.value : undefined
    case FieldDataType.DATE:
      // The edit sends a timestamp, and the card a calendar date.
      if (node.value === null) return null
      return typeof node.value === 'string' ? node.value.slice(0, 10) : undefined
    case FieldDataType.NUMBER:
      return typeof node.numberValue === 'number' || node.numberValue === null ? node.numberValue : undefined
    case FieldDataType.SINGLE_SELECT:
      if (node.name === null) return null
      return toSelectOption(node, currentOptionIds(current))
    case FieldDataType.MULTI_SELECT: {
      if (!Array.isArray(node.options)) return undefined
      const ids = currentOptionIds(current)
      const options: BoardPayloadSelectOption[] = []
      for (const option of node.options) {
        const next = isRecord(option) ? toSelectOption(option, ids) : undefined
        if (next === undefined) return undefined
        options.push(next)
      }
      return options
    }
    default:
      return undefined
  }
}

/**
 * The edit names options by GraphQL node ID, and the card by database ID. The card never reads
 * the ID beyond a React key, so an option the card already shows keeps its ID, and a new one
 * holds 0 until the next board read. The colour is lowercased to match the board payload.
 */
function toSelectOption(
  option: Record<string, unknown>,
  ids: ReadonlyMap<string, number>,
): BoardPayloadSelectOption | undefined {
  if (typeof option.name !== 'string') return undefined
  const color = typeof option.color === 'string' ? option.color.toLowerCase() : 'gray'
  return {id: ids.get(option.name) ?? 0, name: option.name, color}
}

function currentOptionIds(current: unknown): Map<string, number> {
  const options = Array.isArray(current) ? current : [current]
  const ids = new Map<string, number>()
  for (const option of options) {
    if (isRecord(option) && typeof option.name === 'string' && typeof option.id === 'number') {
      ids.set(option.name, option.id)
    }
  }
  return ids
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}
